import momentTz from 'moment-timezone';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useEditApiKey } from 'api/ApiKeyApi';
import { selectUserProjectTimeZone } from 'core/userContext';
import { ApiError } from 'services/api';
import { useModalContext } from 'utils/contexts/modal';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer';
import { fetchApiKeysRequest } from '../actions';
import { ApiKeysFormProvider } from '../contexts/ApiKeysFormProvider';
import { formulateEditApiKeyPayload } from '../utils';
import { ApiKeyModal } from './ApiKeyModal';
import {
  ModalState,
  NAME,
  KEY_TYPE,
  JWT_REQUIRED,
  CUSTOM_EVENT_MANAGE_ALLOW_CREATION,
  CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL,
} from './ApiKeyModal.constants';
import { ApiKey } from '../types';
import { createApiKeySelector } from '../selectors';

export const TEXT = {
  ID: 'api-key-edit-modal',
  BUTTON: 'Save API key',
};

type EditModalProps = {
  apiKey: ApiKey;
};

const EditModal = ({ apiKey }: EditModalProps) => {
  const { hideModal } = useModalContext<ModalState>();
  const { getValues } = useFormContext();
  const dispatch = useDispatch();

  const { mutate } = useEditApiKey({
    onSuccess: () => {
      const name = getValues(NAME);
      dispatch(addPositiveToast(`Successfully updated ${name}.`));
      hideModal();
      dispatch(fetchApiKeysRequest());
    },
    onError: (e: unknown) => {
      const errorText = (e as ApiError)?.response?.data;
      dispatch(addNegativeToast(errorText));
      hideModal();
    },
  });

  const onSubmit = (values: Record<string, any>) => {
    const payload = formulateEditApiKeyPayload(values, apiKey);
    mutate(payload);
  };

  return (
    <ApiKeyModal
      id={TEXT.ID}
      buttonText={TEXT.BUTTON}
      headerText={`Edit ${apiKey[NAME]}`}
      onSubmit={onSubmit}
      disableRequireJwt={true}
      apiKey={apiKey}
    />
  );
};

export const EditApiKeyModal = () => {
  const { modalState } = useModalContext<ModalState>();
  const { keyIdentifier } = modalState as ModalState;
  const projectTimeZone = useSelector(selectUserProjectTimeZone);
  const apiKey = useSelector(createApiKeySelector(keyIdentifier as string));

  const defaultValues = {
    [NAME]: apiKey[NAME],
    [KEY_TYPE]: apiKey[KEY_TYPE],
    [JWT_REQUIRED]: apiKey[JWT_REQUIRED],
    [CUSTOM_EVENT_MANAGE_ALLOW_CREATION]: Boolean(apiKey[CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL]),
    [CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL]: apiKey[CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL]
      ? momentTz.tz(apiKey[CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL], projectTimeZone)
      : momentTz.tz(projectTimeZone).endOf('d'),
  };

  return (
    <ApiKeysFormProvider defaultValues={defaultValues}>
      <EditModal apiKey={apiKey} />
    </ApiKeysFormProvider>
  );
};
